import React, { useEffect } from 'react';
import { GetStaticProps, GetStaticPaths } from 'next';
import LandingPage from '@components/landing/LandingPage';
import { usePageActions, usePageMetrics } from '@cvent/sli-nextjs-metrics';
import { useRouter } from 'next/router';
import { NIL as NIL_UUID } from 'uuid';
import getConfig from 'next/config';
import { useInitAnalytics } from '@hooks/useAnalytics';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { getRudderAnalytics } from '@metrics/rudderAnalytics';
import { parseQueryParam } from '@tools/parseQueryParam';
import getLandingPageProps, { LandingPageProps } from '@server/getLandingPageProps';
import { useWebinarPageActionsApi } from '@metrics/client/react/useWebinarPageActionsApi';
import LandingPageLocalizationProvider from '@components/landing/LandingPageLocalizationProvider';

export function Landing({
  eventInfo,
  environment,
  signupLink,
  embeddedRegistrationUrl,
  appProps,
  reflexEmbeddedRegUrl
}: LandingPageProps): JSX.Element {
  const { id: eventId, defaultLocale, title: eventTitle, speakers, start, end, status, codeSnippets } = eventInfo;
  const { query } = useRouter();
  const errorCode = parseQueryParam(query, 'errorCode');
  const { startActWebinarAttendeeLandingRegistration } = usePageActionsApi();
  const { actWebinarAttendeeLanding } = useWebinarPageActionsApi();

  const { getBaseProps, updateMetricData } = usePageActions();
  useInitAnalytics({
    ...getBaseProps(),
    locale: appProps?.initialLocale,
    eventId,
    environment,
    // This unfortunately is required by either the Analytics' client or the react-analytics context
    accountMappingId: NIL_UUID,
    attendeeId: NIL_UUID,
    hasCodeSnippets: codeSnippets?.length > 0
  });

  const loginUrl = query.environment
    ? `/hub/events/${eventId}/authorize?environment=${query.environment}`
    : `/hub/events/${eventId}/authorize`;
  /**
   * Callback to redirect user to /authorize page to initiate the
   * authenticator flow and send user to attendee-login app
   */
  const onLogin = (): void => {
    window.location.href = loginUrl;
  };

  // Update the current metric data with the event info
  updateMetricData({
    eventName: eventTitle,
    eventStartDate: start,
    eventEndDate: end,
    numberOfSpeakers: speakers?.length,
    status
  });

  /**
   * Effect to track attendee landing on public landing page
   */
  useEffect(() => {
    const rudderAnalytics = getRudderAnalytics();
    if (rudderAnalytics) {
      rudderAnalytics.ready(() => {
        const anonymousId = rudderAnalytics.getAnonymousId();
        // in the landing page, we don't need to send act_webinar_attendee_landing with null anonymousId
        if (anonymousId) {
          actWebinarAttendeeLanding({
            anonymousId,
            eventName: eventTitle,
            eventStartDate: start,
            eventEndDate: end,
            numberOfSpeakers: speakers?.length,
            status
          });
          startActWebinarAttendeeLandingRegistration({ anonymousId, eventName: eventTitle });
        }
      });
    }
  }, [
    actWebinarAttendeeLanding,
    startActWebinarAttendeeLandingRegistration,
    eventTitle,
    start,
    end,
    speakers?.length,
    status
  ]);

  // Track page_load
  usePageMetrics({
    pageIsLoading: (): boolean => false
  });

  return (
    <LandingPageLocalizationProvider locale={defaultLocale} resourceOverrides={eventInfo.resourceOverrides}>
      <LandingPage
        eventInfo={eventInfo}
        onLandingPageLoginClick={onLogin}
        loginUrl={loginUrl}
        signupLink={signupLink}
        embeddedRegistrationUrl={embeddedRegistrationUrl}
        environment={environment}
        errorCode={errorCode}
        reflexEmbeddedRegUrl={appProps.appFeatures.useReflexEmbeddedRegUrl && reflexEmbeddedRegUrl}
      />
    </LandingPageLocalizationProvider>
  );
}

/**
 * Function to get the static props to generate the page
 */
export const getStaticProps: GetStaticProps<LandingPageProps> = async ctx => {
  const { publicRuntimeConfig } = getConfig();

  // Note: we can't use parseEnvironment here since getStaticProps is executed at run time
  // and we don't have access to the request see `/server/parseEnvironment implementation`
  const environment = publicRuntimeConfig.DEFAULT_ENVIRONMENT;

  return getLandingPageProps(ctx, environment);
};

/**
 * Generate static pages dynamically at runtime
 */
export const getStaticPaths: GetStaticPaths = () => ({
  paths: [],
  fallback: 'blocking'
});

export default Landing;
